import { Button, Card, CardContent, Input } from "@exhousing/design-system";
import { Crown, Loader2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  useSelectCandidate,
  useSelectionReasons,
} from "@/hooks/api/useApplications";

/**
 * Journey 7.2: select the candidate with a mandatory objective reason. The backend
 * atomically puts the chosen one under option (14-day offer), moves the best other
 * approved candidate to reserve and rejects the rest.
 */
export function SelectModal({
  applicationId,
  candidateName,
  onClose,
}: {
  applicationId: string;
  candidateName: string;
  onClose: () => void;
}) {
  const { data: reasons } = useSelectionReasons();
  const select = useSelectCandidate();
  const [reasonKey, setReasonKey] = useState("highest_score");
  const [reasonText, setReasonText] = useState("");

  const confirm = () =>
    select.mutate(
      {
        applicationId,
        reasonKey,
        reasonText: reasonKey === "other" ? reasonText.trim() : undefined,
      },
      {
        onSuccess: (result) =>
          toast.success(
            `${candidateName} is under option — offer sent. ` +
              `${result.reserve} reserve, ${result.rejected} rejected.`,
          ) && onClose(),
        onError: (err) => toast.error(err.message),
      },
    );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardContent className="space-y-4 pt-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-display text-xl font-medium">
                Select {candidateName}
              </h2>
              <p className="mt-0.5 text-sm text-muted-foreground">
                The candidate gets a 14-day offer; others move to reserve or are
                rejected automatically. Record the objective reason.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-lg p-1 text-muted-foreground hover:bg-muted"
            >
              <X className="size-5" />
            </button>
          </div>

          <div className="space-y-2">
            {Object.entries(reasons ?? {}).map(([key, label]) => (
              <label
                key={key}
                className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm ${
                  reasonKey === key ? "border-primary bg-primary/5" : "border-border"
                }`}
              >
                <input
                  type="radio"
                  name="selection-reason"
                  checked={reasonKey === key}
                  onChange={() => setReasonKey(key)}
                  className="size-4 accent-primary"
                />
                {label}
              </label>
            ))}
            {reasonKey === "other" && (
              <Input
                placeholder="Objective reason (recorded in the audit trail)"
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
              />
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={
                select.isPending || (reasonKey === "other" && !reasonText.trim())
              }
              onClick={confirm}
            >
              {select.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Crown className="size-4" />
              )}
              Confirm selection
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
